import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Sun, ArrowRight } from "lucide-react";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

const TYPES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "housing_society", label: "Housing Society" },
];

export default function Signup() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    customer_type: "residential",
    establishment_name: "",
    address: "",
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const isOrg = form.customer_type === "commercial" || form.customer_type === "housing_society";

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    if (isOrg && !form.establishment_name.trim()) {
      toast.error("Please enter your establishment / society name");
      return;
    }
    setLoading(true);
    try {
      await register(
        form.name.trim(),
        form.email.trim().toLowerCase(),
        form.password,
        "customer",
        {
          phone: form.phone.trim(),
          customer_type: form.customer_type,
          establishment_name: isOrg ? form.establishment_name.trim() : null,
          address: form.address.trim(),
        }
      );
      toast.success("Account created · Lead submitted");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F4F5F0] crm-grid flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-[#E2E4DC] p-8 rounded-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#FF5C00] flex items-center justify-center">
            <Sun className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-mono text-xs tracking-[0.3em] uppercase">Solar / CRM</div>
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#4A5568]">Customer signup</div>
        <h2 className="font-display text-3xl font-bold text-[#0A1128] mt-2">Track your solar journey.</h2>
        <p className="text-xs text-[#4A5568] mt-2 leading-relaxed">
          Creating your account also files your enquiry with our sales team.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-form">
          {/* Property type — drives the lead-naming rule */}
          <div className="grid grid-cols-3 gap-2" data-testid="signup-type-row">
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setField("customer_type", t.value)}
                data-testid={`signup-type-${t.value}`}
                className={
                  "py-2 text-center font-mono text-[10px] uppercase tracking-widest border transition-colors " +
                  (form.customer_type === t.value
                    ? "border-[#0A1128] bg-[#0A1128] text-white"
                    : "border-[#E2E4DC] text-[#4A5568] hover:border-[#0A1128]")
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          {isOrg && (
            <input
              value={form.establishment_name}
              onChange={(e) => setField("establishment_name", e.target.value)}
              required
              placeholder={form.customer_type === "housing_society" ? "Society / RWA name" : "Company / Establishment name"}
              data-testid="signup-establishment-input"
              className="w-full bg-transparent border-b-2 border-[#0A1128] py-3 focus:outline-none focus:border-[#FF5C00] font-mono"
            />
          )}

          <input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            required
            placeholder={isOrg ? "Contact person name" : "Full name"}
            data-testid="signup-name-input"
            className="w-full bg-transparent border-b-2 border-[#0A1128] py-3 focus:outline-none focus:border-[#FF5C00] font-mono"
          />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            required
            placeholder="Email"
            data-testid="signup-email-input"
            className="w-full bg-transparent border-b-2 border-[#0A1128] py-3 focus:outline-none focus:border-[#FF5C00] font-mono"
          />
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            required
            placeholder="Phone (+91-9XXXXXXXXX)"
            data-testid="signup-phone-input"
            className="w-full bg-transparent border-b-2 border-[#0A1128] py-3 focus:outline-none focus:border-[#FF5C00] font-mono"
          />
          <input
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="City + locality (optional)"
            data-testid="signup-address-input"
            className="w-full bg-transparent border-b-2 border-[#0A1128] py-3 focus:outline-none focus:border-[#FF5C00] font-mono"
          />
          <input
            type="password"
            value={form.password}
            onChange={(e) => setField("password", e.target.value)}
            required
            placeholder="Password"
            minLength={6}
            data-testid="signup-password-input"
            className="w-full bg-transparent border-b-2 border-[#0A1128] py-3 focus:outline-none focus:border-[#FF5C00] font-mono"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="signup-submit-button"
            className="btn-tactile w-full bg-[#FF5C00] text-white py-4 font-mono text-sm uppercase tracking-widest hover:bg-[#0A1128] transition-colors flex items-center justify-center gap-2"
          >
            {loading ? "Creating…" : "Create account"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="mt-6 text-sm text-[#4A5568]">
          Already a user?{" "}
          <Link to="/login" className="underline decoration-[#FF5C00] underline-offset-4 text-[#0A1128]">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
